import type { ProgrammeDayStats } from "@/hooks/api/use-campaign-programmes";

/**
 * How the live board reads a programme's dialling back as days.
 *
 * Apart from the screen for the same reason `../progress.ts` and `../admin/preparation/readiness.ts`
 * are: these are the only claims on the page that are worked out rather than displayed, and a
 * date arithmetic bug reads as a fact about the campaign rather than as a bug.
 */

/** Seconds of connected time as the header reads it — "8h 28m". */
export function formatTalkTime(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds <= 0) return "0m";

	const total = Math.round(seconds / 60);
	const hours = Math.floor(total / 60);
	const minutes = total % 60;

	return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

export function formatRate(rate: number): string {
	if (!Number.isFinite(rate)) return "—";
	return `${rate.toLocaleString("en-SG", { maximumFractionDigits: 1 })}%`;
}

export function formatCount(value: number): string {
	return Number.isFinite(value) ? value.toLocaleString("en-SG") : "—";
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * A `YYYY-MM-DD` bucket printed as the day it names — "Sun 1 Mar".
 *
 * Written out rather than handed to `toLocaleDateString`, because every route through that one
 * carries a timezone the bucket does not have. Parsing the string and rendering it locally puts
 * the whole column a day early west of Greenwich; building it locally and rendering it in UTC does
 * the same east of Greenwich. Each is invisible from the other side of the meridian, so a test can
 * only ever catch one of them — which is how the first shipped. There is no `Date` here to carry a
 * zone at all.
 */
export function formatDay(day: string): string {
	const [year, month, date] = day.split("-").map(Number);
	if (!year || !month || !date) return day;

	// The day and the month are the bucket's own digits. Only the weekday needs counting, and it is
	// counted off the epoch rather than through a `Date` — 1 January 1970 was a Thursday.
	const at = Date.UTC(year, month - 1, date);
	// `Date.UTC` rolls 30 February into March and month 13 into next January, and a weekday printed
	// against a day the calendar does not have reads as a fact ("Mon 30 Feb"). A bucket that is not
	// the day it claims is not a date.
	const rolled = new Date(at);
	if (rolled.getUTCDate() !== date || rolled.getUTCMonth() !== month - 1) return day;

	const epochDay = Math.floor(at / 86_400_000);
	return `${WEEKDAYS[(((epochDay + 4) % 7) + 7) % 7]} ${date} ${MONTHS[month - 1]}`;
}

function dayNumber(day: string): number {
	const [year, month, date] = day.split("-").map(Number);
	return year && month && date ? Date.UTC(year, month - 1, date) : Number.NaN;
}

/**
 * Redials appear as days and are tagged — UX §13 note 3. Nothing on a call says which attempt it
 * was, so the tag is inferred from the shape of the dialing itself: a day that follows a real gap
 * is a return to the same leads, not a continuation. Three days of slack keeps a weekend from
 * being read as a redial.
 */
const REDIAL_GAP_DAYS = 3;

export function isLikelyRedial(days: ProgrammeDayStats[], index: number): boolean {
	if (index === 0) return false;

	const previous = dayNumber(days[index - 1].day);
	const current = dayNumber(days[index].day);
	if (Number.isNaN(previous) || Number.isNaN(current)) return false;

	return (current - previous) / 86_400_000 > REDIAL_GAP_DAYS;
}

/** Inclusive whole days between two ISO dates, or null where either end is missing. */
export function daySpan(from: string | null, to: string | null): number | null {
	if (!from || !to) return null;

	const start = new Date(from).getTime();
	const end = new Date(to).getTime();
	if (Number.isNaN(start) || Number.isNaN(end)) return null;

	return Math.floor((end - start) / 86_400_000) + 1;
}

/**
 * Today, as the `YYYY-MM-DD` bucket the rollup is cut into.
 *
 * "Day 3 of 10" has to be counted in the zone the rollup is stated in, not in the reader's and not
 * in UTC. Every market this runs in is UTC+7 or further east, so counting to `new Date()` in UTC
 * showed the previous day's number for the whole of each local morning — beside a header saying
 * which zone the figures are in.
 */
export function todayIn(zone: string | null | undefined, now: Date = new Date()): string {
	try {
		// `en-CA` is the locale whose short date *is* `YYYY-MM-DD`.
		return new Intl.DateTimeFormat("en-CA", { timeZone: zone || "UTC" }).format(now);
	} catch {
		// An unknown zone is the API's problem to fix; it is not worth a blank screen here.
		return new Intl.DateTimeFormat("en-CA", { timeZone: "UTC" }).format(now);
	}
}
